/**
 * UpgradePrompt Component
 *
 * Reusable banner/inline prompt shown when a feature is locked behind a
 * higher subscription tier.  Provides a consistent look-and-feel everywhere
 * plan gating is enforced so users always understand *why* something is
 * disabled and *how* to unlock it.
 */

import { Lock, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Capability } from '../../hooks/useSubscription'
import { useSubscription } from '../../hooks/useSubscription'
import styles from './UpgradePrompt.module.css'

export interface UpgradePromptProps {
  /** Which capability the user is missing */
  capability: Capability
  /** Optional override for the heading text */
  title?: string
  /** Optional override for the description text */
  description?: string
  /** Visual style — 'banner' (full-width) or 'inline' (compact, fits inside cards) */
  variant?: 'banner' | 'inline'
  /** Where to send the user. Defaults to /settings?tab=billing */
  upgradePath?: string
  /** utm source for pricing page analytics */
  source?: string
}

const CAPABILITY_DEFAULTS: Record<Capability, { title: string; description: string }> = {
  inventoryWrite: {
    title: 'Inventory editing requires a paid plan',
    description: 'Upgrade to the Basic plan or higher to add, edit, and manage your inventory.',
  },
  bulkOps: {
    title: 'Bulk operations require a paid plan',
    description: 'CSV imports, batch sales, and bulk actions are available on Basic, Professional, and Enterprise plans.',
  },
  qrCodes: {
    title: 'QR Codes require Professional',
    description: 'Generate QR codes for your inventory on Professional and Enterprise plans.',
  },
  tradeCenter: {
    title: 'Transactions requires a paid plan',
    description: 'Create and manage transactions on Basic, Professional, and Enterprise plans.',
  },
  calendar: {
    title: 'Calendar requires a paid plan',
    description: 'Upgrade to access the Social Planner calendar and scheduling features.',
  },
  socialPosts: {
    title: 'Social posts require Professional',
    description: 'Create, schedule and publish social posts on Professional and Enterprise plans. The events calendar stays on Basic.',
  },
  storeDisplay: {
    title: 'Store Display requires Professional',
    description: 'Show your stock and prices on an in-store screen on Professional and Enterprise plans.',
  },
  scannerBatch: {
    title: 'Batch scanning requires Professional',
    description: 'Scan a whole pile, review it and add it in one go on Professional and Enterprise plans. Single scans work on every plan.',
  },
  centering: {
    title: 'Centering Check requires Enterprise',
    description: 'Measure a card’s centering from a photo of the front and back on the Enterprise plan.',
  },
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  capability,
  title,
  description,
  variant = 'banner',
  upgradePath,
  source,
}) => {
  const navigate = useNavigate()
  const { requiredTierFor, tierName, tier } = useSubscription()

  const defaults = CAPABILITY_DEFAULTS[capability]
  const requiredTier = requiredTierFor(capability)
  const requiredLabel = tierName(requiredTier)
  const currentTierLabel = tierName(tier)

  const heading = title ?? defaults.title
  const desc = description ?? defaults.description

  const target = upgradePath ?? `/settings?tab=billing`
  const fullTarget = source ? `${target}${target.includes('?') ? '&' : '?'}source=${source}` : target

  return (
    <div className={`${styles.prompt} ${styles[variant]}`} role="alert">
      <div className={styles.iconWrap}>
        <Lock size={variant === 'inline' ? 16 : 20} />
      </div>
      <div className={styles.body}>
        <strong className={styles.heading}>{heading}</strong>
        <p className={styles.desc}>{desc}</p>
        <p className={styles.desc}>Locked by subscription tier. Current: {currentTierLabel} · Required: {requiredLabel}</p>
      </div>
      <button
        type="button"
        className={styles.upgradeBtn}
        onClick={() => navigate(fullTarget)}
      >
        Upgrade to {requiredLabel}
        <ArrowRight size={14} />
      </button>
    </div>
  )
}

export default UpgradePrompt
